import { useEffect, useRef, useState, type FormEvent } from 'react';
import { guidedAdapter } from '../../assistant/adapter.js';
import { restartIntake } from '../../intake/engine.js';
import {
  AGE_OPTIONS, GOAL_OPTIONS, INDUSTRY_OPTIONS, NODE_BY_ID, STAGE_OPTIONS, type Answer, type IntakeNode,
} from '../../intake/graph.js';
import { fmtMoney, parseAmount, parseCount } from '../../intake/parseAnswer.js';
import { describeSource } from '../../core/financials.js';
import type { Fact, Workspace } from '../../workspace/schema.js';
import { PageHead, Progress, Term } from '../components.js';
import { useWorkspace } from '../state.js';

const adapter = guidedAdapter;

function AnswerForm({ node, ws, onAnswer }: {
  node: IntakeNode; ws: Workspace; onAnswer: (a: Answer) => void;
}) {
  const [text, setText] = useState('');
  const [multi, setMulti] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { setText(''); setMulti([]); setError(null); inputRef.current?.focus(); }, [node.id]);

  const preview = node.type === 'amount' && text.trim() ? parseAmount(text) : null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (node.type === 'amount') {
      const p = parseAmount(text);
      if (!p) { setError('I could not read an amount there. Try something like 150k or $1.2m.'); return; }
      if (p.value < 0 && node.id !== 'operating-income') { setError('That needs to be zero or more.'); return; }
      onAnswer({ kind: 'number', value: p.value, approximate: p.approximate, ...(p.note ? { note: p.note } : {}) });
    } else if (node.type === 'count') {
      const n = parseCount(text);
      if (n === null) { setError('Enter a whole number, like 6.'); return; }
      onAnswer({ kind: 'number', value: n });
    } else if (node.type === 'text') {
      if (!text.trim()) { setError('Add a few words, or skip for now.'); return; }
      onAnswer({ kind: 'text', value: text.trim().slice(0, 300) });
    }
  };

  const opts = node.options?.(ws) ?? [];

  if (node.type === 'choice' || node.type === 'industry') {
    return (
      <div className="chips" role="group" aria-label="Choose an answer">
        {opts.map((o) => (
          <button key={o.value} type="button" className="chip" onClick={() => onAnswer({ kind: 'choice', value: o.value })}>
            {o.label}{o.hint && <small>{o.hint}</small>}
          </button>
        ))}
      </div>
    );
  }

  if (node.type === 'multi') {
    const none = multi.length === 0;
    return (
      <div className="stack">
        <div className="chips" role="group" aria-label="Choose any that apply">
          {opts.map((o) => {
            const on = multi.includes(o.value);
            return (
              <button key={o.value} type="button" className="chip" aria-pressed={on}
                onClick={() => setMulti((m) => (on ? m.filter((x) => x !== o.value) : [...m, o.value]))}>
                {on ? '✓ ' : ''}{o.label}
              </button>
            );
          })}
        </div>
        <div className="row">
          <button type="button" className="btn primary" onClick={() => onAnswer({ kind: 'multi', values: multi })}>
            {none ? 'None of these yet' : `Continue with ${multi.length}`}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate>
      <label className="sr-only" htmlFor={`a-${node.id}`}>{node.prompt(ws)}</label>
      <input
        ref={inputRef}
        id={`a-${node.id}`}
        className="input"
        inputMode={node.type === 'text' ? 'text' : 'decimal'}
        autoComplete="off"
        placeholder={node.placeholder ?? (node.type === 'amount' ? 'e.g. 150k' : '')}
        value={text}
        onChange={(e) => { setText(e.target.value); setError(null); }}
        aria-invalid={!!error}
        aria-describedby={`h-${node.id}`}
      />
      <button className="btn primary" type="submit">Continue</button>
      <div id={`h-${node.id}`} style={{ flexBasis: '100%' }}>
        {error
          ? <span className="error-text" role="alert">{error}</span>
          : preview
            ? <span className="hint">Reads as <strong>{fmtMoney(preview.value)}</strong>{node.unit === 'monthly' ? ' a month' : node.unit === 'annual' ? ' a year' : ''}{preview.note ? ` — ${preview.note}` : ''}</span>
            : <span className="hint">&nbsp;</span>}
      </div>
    </form>
  );
}

const label = (opts: { value: string; label: string }[], v: string) => opts.find((o) => o.value === v)?.label ?? v;

function KnownSoFar({ ws, onChange }: { ws: Workspace; onChange: (nodeId: string) => void }) {
  const p = ws.profile;
  const money = (f?: Fact<number>, suffix = '') => (f ? `${fmtMoney(f.value)}${suffix}` : null);
  const rows: Array<{ k: string; v: string | null; node: string; src?: string }> = [
    { k: 'Goal', v: p.goal ? label(GOAL_OPTIONS, p.goal.value) : null, node: 'goal' },
    { k: 'Stage', v: p.stage ? label(STAGE_OPTIONS, p.stage.value) : null, node: 'stage' },
    { k: 'Industry', v: p.industry ? label(INDUSTRY_OPTIONS, p.industry.value) : null, node: 'industry' },
    { k: 'Operating for', v: p.ageBand ? (p.ageBand.value === 'not-operating' ? 'Not yet operating' : label(AGE_OPTIONS, p.ageBand.value)) : null, node: 'age' },
    { k: 'Amount', v: money(p.amount), node: 'amount' },
    { k: 'Price', v: money(p.purchasePrice), node: 'purchase-price' },
    { k: 'Your own money in', v: money(p.ownerInjection), node: 'owner-injection' },
    { k: 'Use of funds', v: p.fundsDescription?.value ?? null, node: 'funds-description' },
    { k: 'Sales (12 mo)', v: money(p.annualRevenue), node: 'revenue', ...(p.annualRevenue ? { src: describeSource(p.annualRevenue) } : {}) },
    { k: 'Operating income', v: money(p.operatingIncome), node: 'operating-income', ...(p.operatingIncome ? { src: describeSource(p.operatingIncome) } : {}) },
    { k: 'Loan payments today', v: p.existingAnnualDebtService ? `${fmtMoney(p.existingAnnualDebtService.value / 12)}/mo` : null, node: 'existing-debt' },
    { k: 'Jobs supported', v: p.jobs ? String(p.jobs.value) : null, node: 'jobs' },
    { k: 'Repayment length', v: p.termMonths ? `${p.termMonths.value / 12} years` : null, node: 'term' },
  ];
  const shown = rows.filter((r) => r.v !== null);
  return (
    <div className="card known">
      <h2 style={{ fontSize: '1.1rem' }}>What I know so far</h2>
      {shown.length === 0
        ? <p className="muted small">Your answers will build up here. Change any of them at any time.</p>
        : (
          <dl>
            {shown.map((r) => (
              <div className="item" key={r.k}>
                <dt>{r.k}</dt>
                <dd>
                  {r.v}
                  {r.src && <small>{r.src}</small>}
                  {NODE_BY_ID.has(r.node) && <button type="button" className="link-btn small" onClick={() => onChange(r.node)}>Change</button>}
                </dd>
              </div>
            ))}
          </dl>
        )}
    </div>
  );
}

export function Assistant() {
  const { ws, update } = useWorkspace();
  const turn = adapter.current(ws);
  const logEnd = useRef<HTMLDivElement>(null);
  const node = turn.node;

  useEffect(() => { logEnd.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); }, [ws.intake.transcript.length]);

  return (
    <>
      <PageHead eyebrow="Guided assistant" title="Let’s build your financing profile">
        One question at a time. Skip anything you don&rsquo;t know — you can come back to it.
      </PageHead>

      <div className="split">
        <section className="chat" aria-label="Conversation">
          <Progress value={turn.progress.done} max={turn.progress.total} label="Questions answered" />
          <div className="chat-log" aria-live="polite">
            {ws.intake.transcript.length === 0 && (
              <div className="bubble assistant">
                Hi — I&rsquo;ll help you organise your financing request, find what you might be missing, and prepare for a
                lender conversation. Nothing you type leaves this device.
              </div>
            )}
            {ws.intake.transcript.map((m) => (
              <div key={m.id} className={`bubble ${m.role}`}>
                {m.text}
                {m.role === 'user' && m.nodeId && node?.id !== m.nodeId && (
                  <button type="button" className="link-btn edit" onClick={() => update((w) => adapter.revisit(w, m.nodeId!))}>Change</button>
                )}
              </div>
            ))}
            <div ref={logEnd} />
          </div>

          {node ? (
            <div className="ask">
              <div className="q" id="current-q">{node.prompt(ws)}</div>
              <div className="why">
                {node.why}
                {node.terms && node.terms.length > 0 && (
                  <span style={{ display: 'block', marginTop: 6 }}>
                    {node.terms.map((t, i) => <span key={t}>{i > 0 && ' · '}<Term id={t} /></span>)}
                  </span>
                )}
              </div>
              <AnswerForm
                node={node}
                ws={ws}
                onAnswer={(a) => update((w) => adapter.respond(w, node.id, a))}
              />
              <div className="ask-foot">
                <button type="button" className="link-btn small" onClick={() => update((w) => adapter.skip(w, node.id))}>
                  Skip for now
                </button>
                <span className="faint small">Question {Math.min(turn.progress.done + 1, turn.progress.total)} of about {turn.progress.total}</span>
              </div>
            </div>
          ) : (
            <div className="card glow">
              <h2>Your profile is set up</h2>
              <p className="muted">Here&rsquo;s where to go next. You can change any answer from the panel or the conversation.</p>
              <div className="row">
                <a className="btn primary" href="#/documents">Upload documents</a>
                <a className="btn" href="#/estimate">See the estimate</a>
                <a className="btn" href="#/scenarios">Compare scenarios</a>
                <a className="btn" href="#/plan">Action plan</a>
              </div>
              {ws.intake.skipped.length > 0 && (
                <p className="small muted" style={{ marginTop: 12 }}>
                  You skipped {ws.intake.skipped.length} question{ws.intake.skipped.length === 1 ? '' : 's'}. Use
                  &ldquo;Change&rdquo; next to any answer, or{' '}
                  <button type="button" className="link-btn" onClick={() => update((w) => ({ ...w, intake: { ...w.intake, skipped: [] } }))}>
                    answer skipped questions now
                  </button>.
                </p>
              )}
            </div>
          )}
        </section>

        <aside className="stack">
          <KnownSoFar ws={ws} onChange={(id) => update((w) => adapter.revisit(w, id))} />
          <div className="card flat">
            <h3 style={{ fontSize: '0.95rem' }}>About this assistant</h3>
            <p className="small muted">
              It follows a fixed set of questions written to adapt to your answers, so it never makes things up. A version
              you can ask anything in your own words is planned.
            </p>
            {ws.intake.transcript.length > 0 && (
              <button type="button" className="link-btn small" onClick={() => {
                if (window.confirm('Start the questions over? Your documents and scenarios are kept.')) update(restartIntake);
              }}>Start the questions over</button>
            )}
          </div>
        </aside>
      </div>
    </>
  );
}
